import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AppHeader from "../../Comp/AppHeader";
import EmailLink from "../../Comp/EmailLink";
import pdfReader from "./assets/pdfReader.png";

const policyContent = `Privacy Policy
Last Updated: May 11, 2026
Page Bloom is a PDF Reader app developed by Photo Editor & Video Editor - Merger IO, operated under AppSpace Solutions. We respect and protect users’ personal privacy. Please carefully read this Privacy Policy before using our products and services.

Page Bloom does not require you to create an account or register to use the App. You can access all features directly without providing any identifying information, unless you choose to contact us for support or opt into optional services.

Please read this policy carefully. If you disagree with its terms, please discontinue use of the App. By using Page Bloom, you consent to the data practices described herein.

Overview
Information We Collect
How We Use Information
Legal Basis for Processing (GDPR)
Application Permission Requirements
How We Share Your Information
Data Retention
Data Security
Your Privacy Rights
Children’s Privacy
Data Privacy Officer
Changes to This Privacy Policy
Contact Us

Information We Collect

1.1 Information You Provide Directly
When you use our App, you may provide information, including:
•        Documents and Files: PDFs, images, Word files, and scanned documents that you select, convert, sign, or process using our features.
•        Digital Signatures: Handwritten or typed signatures created using the Sign feature.
•        Encryption Credentials: Passwords you set when using the Lock PDF feature (stored locally on your device; we do not retain these).
•        Payment Information: When you purchase a premium plan, billing is handled entirely by Google Play's In-App Purchase system. We receive only a confirmation of the transaction (e.g., purchase token and subscription status). We never see, receive, or store your payment card details, bank information, or any other financial credentials.
•        Feedback and Support: Information such as your name or email address that you voluntarily provide when submitting support requests, surveys, or in-app feedback.

1.2 Information Collected Automatically
When you use the App, we may automatically collect:
•        Device Information: Device model, operating system version, unique device identifiers, and hardware configuration.
•        Usage Data: Features accessed, files processed (by type and size only), session duration, crash logs, and performance metrics.
•        Log Data: IP address, browser type (if web version), date/time stamps, and referring URLs.
•        Camera and Microphone Access: Accessed only when you use the Scan to PDF feature and only with your explicit permission.
•        Local Storage Data: Temporary cache of processed documents stored on your device for operational purposes.

1.3 Information We Do NOT Collect
We are committed to data minimization. We do not:
•        Read, store, or transmit the content of your personal documents beyond what is necessary to perform the requested feature.
•        Store your PDF passwords or encryption keys anywhere. They exist only on your device and are never transmitted.
•        Access your device's gallery, file system, or camera beyond what you explicitly authorize.
•        Sell your personal information to third parties.
2. How We Use Your Information
We use the information we collect for the following purposes:
2.1 Service Delivery
•        To process documents through features including Image to PDF, Scan to PDF, Word to PDF, Merge PDF, Split PDF, Compress PDF, Print PDF, Lock PDF, and Extract Text.
•        To enable digital signing and signature storage (locally on your device unless cloud sync is enabled).
•        To deliver, maintain, and improve the App's core functionality.
2.2 Security & Fraud Prevention
•        To detect and prevent fraudulent or abusive use of the App.
•        To enforce our Terms of Service.
•        To protect the integrity and security of our systems and services.
2.3 Communication
•        To send transactional communications (e.g., purchase confirmations) if you make an in-app purchase.
•        To respond to support requests or feedback you submit to us.
•        To send promotional communications only with your explicit consent, which you may withdraw at any time.
2.4 Analytics & Improvement
•        To analyze usage patterns to improve features and user experience.
•        To diagnose technical issues and resolve bugs.
•        To conduct internal research on App performance.
2.5 Legal Compliance
•        To comply with applicable laws, regulations, and legal processes.
•        To respond to lawful requests from public authorities.
•        To enforce our legal rights and agreements.
3. Legal Basis for Processing (GDPR)
For users in the European Economic Area (EEA), United Kingdom, and Switzerland, we process your personal data only where we have a valid legal basis under Article 6 of the General Data Protection Regulation (GDPR). The legal basis we rely on, what it covers, and how you can opt out are set out below:
3.1 Legitimate Interests (Art. 6(1)(f))
We process certain data to pursue our legitimate interests in operating a secure, functional, and improving App ensuring those interests are not overridden by your rights and freedoms.
This covers: App performance diagnostics, crash reporting, anonymized usage analytics, and security/fraud prevention measures.
Your opt-out right: You have the right to object to processing based on legitimate interests at any time (see Section 7.1 — Right to Object). To object, email feedback@appspacesolutions.in with the subject line "GDPR Objection". We will cease the processing unless we can demonstrate compelling legitimate grounds that override your interests.
3.2 Consent (Art. 6(1)(a))
Where we rely on your consent, we will always ask for it clearly and separately before processing begins. This covers: optional analytics tracking, and camera access for the Scan to PDF feature.
How to withdraw consent:
◦    Analytics: Go to App Settings → Privacy → Analytics and toggle off "Share Usage Data".
◦        Camera access: Go to your device's system Settings → Apps → Page Bloom → Permissions and revoke Camera access at any time.
Withdrawing consent does not affect the lawfulness of processing carried out before the withdrawal.
3.3 Contractual Necessity (Art. 6(1)(b))
When you make an in-app purchase, your payment is processed entirely by Google Play's In-App Purchase (IAP) system. We receive only a confirmation token and your subscription status to unlock premium features. We do not process or store any payment credentials ourselves. This limited data processing is necessary to verify and activate your purchase. You may opt out by choosing not to make a purchase.
3.4 Legal Obligation (Art. 6(1)(c))
We may process data where necessary to comply with a legal obligation, such as tax record-keeping or responding to a lawful request from a public authority. You cannot opt out of processing that is strictly required by law; however, we will only process the minimum data necessary to fulfil that obligation.
For special categories of data (if applicable), we rely on explicit consent (Art. 9(2)(a)) or the establishment, exercise, or defence of legal claims (Art. 9(2)(f)).
4. Application Permission Requirements
To deliver the full range of Page Bloom’s features, the App requests certain device permissions. We only request permissions that are directly required to make a specific feature work, and we never use a granted permission for any purpose beyond what is described below.
You are always in control. Every permission can be granted or revoked at any time through your device's system Settings → Apps → Page Bloom → Permissions. Revoking a permission will disable the feature that depends on it, but will not affect any other part of the App.
4.1 All Files Access Permission
Why we need it: Page Bloom is built around document management — finding, previewing, organizing, editing, and converting files stored on your device. The All Files Access permission allows the App to browse your device's storage so you can open any PDF, image, or Word file regardless of where it is saved, without any restriction.
Without this permission, you won’t be able to use the App.
What we don't do: This permission is used exclusively within the App for your own file management on your device. We do not scan, index, upload, or transmit the contents of your storage anywhere. We do not access files unrelated to an action you explicitly initiate.
How to manage: Go to device Settings → Apps → Page Bloom → Permissions → Files and Media. You can revoke this at any time; file browsing features will revert to single-file selection mode.
4.2 Camera Permission
Why we need it: The Camera permission powers the Scan to PDF feature, which lets you use your device's camera to photograph physical documents like receipts, contracts, handwritten notes, ID cards, and more. It instantly converts them into a clean, shareable PDF.
The camera is activated only when you deliberately open the scanner within the App by tapping the Scan button. We do not access the camera in the background, during other features, or at any time you have not explicitly triggered scanning.
What we don't do: We do not capture images passively, record video, access your camera roll without your action, or transmit your camera feed anywhere. All scanning and conversion is performed entirely on your device. Your scanned images never leave your device unless you explicitly choose to share or export the resulting PDF yourself.
How to manage: Go to device Settings → Apps → Page Bloom → Permissions → Camera. Revoking this permission disables the Scan to PDF feature; all other App features remain fully available.
4.3 Notifications Permission
For devices with Android 13 and above, with your permission, we may send notifications to inform you of important events, messages, news, or updates. You can turn these notifications on or off at any time in your device settings. We do not use notifications for unsolicited advertising.

How to manage: Go to device Settings → Apps → Page Bloom → Notifications, or navigate within the App to Settings → Notifications. You can fine-tune which notification categories you receive (e.g., allow task alerts but disable tips) without revoking the permission entirely.
5. How We Share Your Information
We do not sell, rent, or trade your personal information. We may share information in the following limited circumstances:
5.1 Service Providers
We engage trusted third-party vendors who assist in operating the App. All service providers are bound by strict contractual data protection obligations and are permitted to use your data only for the specific purpose for which they were engaged. The categories of service providers we work with are described below:
5.1.1 Analytics Providers
We use analytics tools to understand how users interact with the App — such as which features are used most, how long sessions last, and where users encounter difficulties. This helps us make the App faster, more intuitive, and more reliable over time.
What data is shared: Anonymized and aggregated usage events (e.g., feature tapped, session duration, screen views). We do not share the content of your documents with analytics providers. Device identifiers may be used in a pseudonymized form.
Providers we use for Analytics are as follows-
Facebook Analytics (by Facebook Inc.)
We use Facebook Analytics for in-game data analytics to improve our games.
For more information on the privacy practices of Facebook, please visit Facebook’s Data Policy: https://www.facebook.com/privacy/explanation

Firebase Analytics
Google Firebase offers a variety of features, which can be found on the following summary page: https://firebase.google.com/products/

The functions include the storage of apps, including users’ personal data, such as content they have created or information regarding their interaction with the apps. Google Firebase also offers interfaces that allow interaction between the users of the app and other services.
The analysis of user interactions is carried out using the analysis service of Firebase Analytics. This service helps us to record our users’ interactions. Events such as the first time an app is opened, the uninstalling of an app, updates, crashes, or the frequency of use of the app are recorded. Certain user interests are also recorded and evaluated.
The legal basis for use is Art. 6 paragraph 1 sentence 1 letter of GDPR. If users wish to object to interest-based advertising through Google marketing services, they can use  https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings
The information processed by Google Firebase may be used with other Google services, such as Google Analytics and Google marketing services. In this case, only pseudonymous information, such as the Android Advertising ID or the Advertising Identifier for iOS, will be processed to identify users’ mobile devices. Additional information on the use of data for marketing purposes by Google can be found on the summary page: https://policies.google.com/technologies/ads . Google’s privacy policy is available at https://policies.google.com/privacy

Tenjin.io (by Tenjin, Inc.)
We use Tenjin for performance attribution.
For more information on what type of information Tenjin collects, please visit: https://tenjin.com/privacy/

Adjust (by Adjust GmbH.)
We use Adjust for performance attribution.
For more information on what type of information Adjust collects, please visit: https://www.adjust.com/terms/privacy-policy/

AppsFlyer (by AppsFlyer)
We use AppFlyer for performance attribution.
For more information on what type of information AppsFlyer collects, please visit: https://www.appsflyer.com/legal/privacy-policy/
Your opt-out: You can opt out of analytics data collection at any time by going to App Settings → Privacy → Analytics and toggling off "Share Usage Data".
On Android, go to Settings → Google → Ads → Opt out of Ads Personalization.
5.1.2 Crash Reporting (Crashlytics)
We use Crashlytics (a Firebase product by Google) to detect and report App crashes and errors automatically. When the App crashes or encounters a critical error, Crashlytics captures a diagnostic report so our engineering team can identify the cause and issue a fix quickly.
What data is shared: Device model, operating system version, App version, a stack trace describing the error, and a pseudonymized device identifier. Crashlytics does not capture the content of any documents you were working on at the time of the crash.
How Crashlytics uses data: Crash reports are used solely for debugging and App stability purposes. Google's data processing terms apply. You can review Google's privacy policy at https://policies.google.com/privacy .
Your opt-out: You can disable crash reporting by going to App Settings → Privacy → Diagnostics and toggling off "Send Crash Reports". Note that opting out means we will not be alerted to crashes you experience, which may delay fixes for issues affecting you.
5.1.3 Advertising Providers
To support the free version of Page Bloom, we work with advertising partners to display ads within the App. Advertising allows us to keep the App available at no cost while continuing to develop and maintain it.
We do not share your name, email, or document contents with advertising providers.
Interest-based advertising: Our advertising partners may use your advertising identifier to show you ads relevant to your interests based on your activity across other apps and websites. This is known as interest-based or targeted advertising.
Providers we use for Advertising are as follows-
IronSource (by IronSource Mobile Ltd.)
You can opt out of the IronSource network by following the instructions as described by IronSource on their Privacy Policy page. Check the link for more information- https://developers.is.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/

MoPub (Parent company- AppLovin)
You can opt out of the MoPub service by following the instructions on the MoPub Opt-out page: https://dash.applovin.com/p/how-applovin-shows-you-ads
For more information on what information MoPub collects and how it is used, please read MoPub Privacy Policy: https://www.applovin.com/privacy/

Facebook Audience Network (by Facebook Inc.)
For more information about Facebook Audience Network, please visit their Policy: https://developers.facebook.com/docs/audience-network/optimization/best-practices/an-policy/

AdMob (by Google Inc.)
Understand how “the end-user data collected by Google Mobile Ads SDK” from the link below-
https://developers.google.com/admob/android/privacy/play-data-disclosure

You can opt out from the AdMob by Google service by following the instructions described by Google: https://support.google.com/My-Ad-Center-Help/answer/12155764?hl=en
For more information on how Google uses the collected information, please visit the “How Google uses data when you use our partners’ sites or app” page: https://policies.google.com/technologies/partner-sites or visit the Privacy Policy of Google: https://policies.google.com/privacy

AppLovin (by AppLovin Corporation)
If you’d like to opt out of AppLovin service, please visit the AppLovin Opt-out page: https://dash.applovin.com/p/how-applovin-shows-you-ads

If you’d like to learn more about AppLovin, please visit the Privacy Policy page of AppLovin:https://www.applovin.com/privacy/

HyprMx (by HyprMX Mobile LLC.)
For more information about HyprMX, please visit their Privacy Policy: https://jungroup.com/privacy-policy

Pangle (by Bytedance Pte.Ltd.)
For more information about Pangle, please visit their Privacy Policy: https://ad.oceanengine.com/union/media/privacy

Fyber (now Digital Turbine)
For more information about Fyber, please visit their Privacy Policy: https://www.digitalturbine.com/legal/privacy-policy

Vungle (by Vungle Inc.)
You can opt out of Vungle service by following the instructions as described by Vungle on their Privacy Policy page: https://vungle.com/privacy/
For more information about Vungle, please visit their Privacy Policy: https://vungle.com/privacy/

Mintegral (by Mintegral International Ltd.)
For more information about Mintegral, please visit their Privacy Policy: https://www.mintegral.com/en/privacy/

Ogury (by Ogury Limited)
For more information about Ogury, please visit their Privacy Policy: https://ogury.com/ogury-advertising-privacy-policy/

Tapjoy (by Tapjoy Inc.)
You can opt-out of Tapjoy service by following the instructions as described by Tapjoy on their Privacy Policy page: https://dev.tapjoy.com/en/legal/Advertiser-Terms-Of-Service
For more information about Tapjoy, please visit their Privacy Policy: https://dev.tapjoy.com/en/legal/Privacy-Policy

Chartboost (by Chartboost Inc.)
For more information about Chartboost, please visit their Privacy Policy: https://docs.chartboost.com/en/legal/privacy-policy/

AdColony (by Jirbo Inc.)
You can opt out of AdColony service, check the link below for more information: https://www.digitalturbine.com/legal/do-not-sell-or-share-my-personal-information-website

You can also opt out of AdColony through the Digital Advertising Alliance in the USA https://optout.aboutads.info/?c=2&lang=EN or opt-out using your mobile device settings.
For more information about AdColony, please visit their Privacy Policy: https://www.digitalturbine.com/legal/do-not-sell-or-share-my-personal-information-website

InMobi (by InMobi Pvt. Ltd.)
You can opt out of the InMobi service by following the instructions as described on the InMobi Opt-out page: https://advertising.inmobi.com/page/opt-out
For more information on the privacy practices and policies of InMobi, please visit the InMobi Privacy Policy: https://advertising.inmobi.com/privacy-policy

Start.io (by Start.io Inc.)
You can opt out of interest-based ads served by Start.io Inc. (formerly known as StartApp) by following the instructions on their Privacy Policy, section “8. Your Choices and Controls”: https://www.start.io/policy/privacy-policy/

For more information on what information Start.io collects and how it is used, please read their Privacy Policy: https://www.start.io/policy/privacy-policy/

Smaato (by Smaato, Inc.)
For more information on what information Smaato collects and how it is used, please read their Privacy Policy: https://www.smaato.com/privacy/

Verizon (by Verizon Communications Inc.)
For more information on what information Verizon collects and how it is used, please read their Privacy Policy: https://www.verizon.com/about/privacy/

Google AdSense & DoubleClick Cookie
Google, as a third-party vendor, uses cookies to serve ads on our Service. Google’s use of the DoubleClick cookie enables it and its partners to serve ads to our users based on their visit to our Service or other websites on the Internet.
You may opt out of the use of the DoubleClick Cookie for interest-based advertising by visiting the Google Ads Settings web page: https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings
Your opt-out options:
◦        In-App: Go to App Settings → Privacy → Advertising and toggle on "Limit Ad Tracking" to opt out of interest-based ads. You will still see ads, but they will be non-personalized.
◦        Android: Go to device Settings → Google → Ads → select "Delete advertising ID" or toggle on "Opt out of Ads Personalization".
EU/UK/EEA users: You will be shown a consent prompt before any personalized advertising begins, in compliance with GDPR and the ePrivacy Directive. You may withdraw consent at any time via App Settings → Privacy → Advertising.

Premium users: If you have purchased a premium subscription, ads are removed entirely and no advertising data is shared with ad providers.
5.1.4 Behavioural Remarketing
The App uses remarketing services to advertise on third-party websites to you after you visit our Service. Our third-party vendors use cookies to inform, optimize, and serve ads based on your past visits to our Service.

Google Ads
Google AdWords remarketing service is provided by Google Inc.
You can opt out of Google Analytics for Display Advertising and customize the Google Display Network ads by visiting the Google Ads Settings page: https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings

Google also recommends installing the Google Analytics Opt-out Browser Add-on – https://tools.google.com/dlpage/gaoptout – for your web browser.

Google Analytics Opt-out Browser Add-on provides visitors with the ability to prevent their data from being collected and used by Google Analytics.

For more information on the privacy practices of Google, please visit the Google Privacy & Terms web page: https://policies.google.com/privacy?hl=en

Facebook Ads
Facebook remarketing service is provided by Facebook Inc.
You can learn more about interest-based advertising from Facebook by visiting this page: https://www.facebook.com/business/help/488043719226449?id=434838534925385
To opt out of Facebook’s interest-based ads, follow these instructions from Facebook: https://www.facebook.com/help/568137493302217
Facebook adheres to the Self-Regulatory Principles for Online Behavioural Advertising established by the Digital Advertising Alliance. You can also opt-out from Facebook and other participating companies through the Digital Advertising Alliance in the USA https://optout.aboutads.info/?c=2&lang=EN , the Digital Advertising Alliance of Canada in Canada https://optout.aboutads.info/?c=2&lang=EN or the European Interactive Digital Advertising Alliance in Europe http://www.youronlinechoices.eu/ , or opt-out using your mobile device settings.
For more information on the privacy practices of Facebook, please visit Facebook’s Data Policy: https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0

AppLovin (by AppLovin Corporation)
If you’d like to opt out of AppLovin service, please visit the AppLovin Opt-out page: https://dash.applovin.com/p/how-applovin-shows-you-ads
If you’d like to learn more about AppLovin, please visit the Privacy Policy page of AppLovin:https://www.applovin.com/privacy/
5.1.5 Other Service Providers
We also engage the following categories of providers for operational purposes:
•        Google Play In-App Purchases: All payments for premium features are processed exclusively through Google Play's In-App Purchase (IAP) system. Google handles the entire payment transaction, including the collection and storage of your payment card details. We receive only a purchase confirmation token and subscription status. We never have access to your card number, bank details, or any financial credentials. Google's payment handling is governed by the Google Play Terms of Service and Google's Privacy Policy (https://policies.google.com/privacy).
5.1.6 Other Social Media Platforms
We may use your information to send you marketing and promotional communications about our apps and services. This includes updates, special offers, and news related to our products. You may receive these communications through various channels, including email, push notifications, or in-app messaging.

To promote our apps and engage with users, we also use a variety of social media platforms such as:

Facebook -
https://www.facebook.com/appspacesolutionss
Instagram -
https://www.instagram.com/appspaceoffical?igsh=c3Zsazh2bmlpMmxx
X (formerly Twitter) -
https://twitter.com/AppSpaceSolu
LinkedIn -
https://www.linkedin.com/company/appspace-solutions/
YouTube -
https://www.youtube.com/@appspacesolutions
Pinterest -
https://in.pinterest.com/appspacesolutions/

These platforms allow us to share updates, features, and promotional content, and interact with users who follow our accounts. Please note that while we use these platforms to advertise and promote our apps, we do not share your personal information with these social networks unless you specifically opt in or interact with our content directly on their platforms.

If you do not wish to receive marketing communications, you can opt out by following the unsubscribe instructions provided in any email or contacting us directly.
5.2 Legal Requirements
We may disclose your information if required to do so by law or in response to valid legal requests by public authorities, including to meet national security or law enforcement requirements.
5.3 Business Transfers
If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will provide prominent notice within the App prior to your information becoming subject to a different privacy policy.
5.4 With Your Consent
We may share your information for any other purpose with your explicit prior consent.
6. Data Retention
We retain information only for as long as necessary to fulfill the purposes outlined in this policy. Since no account is required, we do not maintain persistent user profiles. Specific retention periods are as follows:
•        Processed Documents: All document processing (conversion, merging, splitting, compressing, signing, and extracting text) is performed locally on your device. No document data is ever sent to or stored on any external server by us.
•        Usage & Analytics Data: Anonymized and aggregated analytics events collected via third-party SDK (e.g., Firebase Analytics) are retained per Google's data retention policies, typically up to 14 months.
•        Crash Reports: Diagnostic crash data collected via Crashlytics is retained per Google Firebase's data retention policies.
•        Support Correspondence: Any personal information you provide when contacting support (e.g., email, device info) is retained for up to 36 months for quality assurance.
•        Payment Records: Transaction records retained as required by financial regulations, typically 5–7 years.
•        Legal & Compliance Records: Retained as required by applicable law.
Because we do not maintain user accounts, there is no account deletion process. If you have submitted a support request or made a purchase and wish to request deletion of associated data, please contact us at feedback@appspacesolutions.in.
7. Data Security
We implement industry-standard technical and organizational security measures to protect your personal information:
•        On-Device Processing: Every conversion, merging, splitting, compressing, signing, locking, and text extraction runs locally on your device. Your files are never transmitted to any server operated by us. We do not have any server.
•        Lock PDF Encryption: Password protection for PDFs is enforced using AES-128/256 bit encryption entirely on your device. Your passwords are never shared with us or any third party.
•        Third-Party SDK Security: Analytics (Firebase) and crash reporting (Crashlytics) SDKs transmit only anonymized, non-document data to Google's infrastructure over encrypted TLS connections. These SDKs are governed by Google's security standards.
•        Local Storage Integrity: Temporary working files created during processing are stored in the App's sandboxed private storage directory and are inaccessible to other apps on your device.
•        No Credential Storage: We do not store passwords, encryption keys, or payment credentials anywhere. These remain exclusively with you or with Google Play, respectively.
8. Your Privacy Rights
We want you to be in full control of your personal data. This section explains your rights under each applicable law and exactly how to exercise them. We will always respond free of charge and without requiring you to justify your request.
8.1 Rights Under GDPR (EEA, UK & Switzerland Users)
If you are in the EEA, UK, or Switzerland, the GDPR and UK GDPR give you the following rights. Each right is explained along with how to exercise it:
•        Right of Access (Art. 15): You can request a copy of all personal data we hold about you, along with information about how we use it, who we share it with, and how long we keep it. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Access Request". We will respond within 30 days with a data summary.
•        Right to Rectification (Art. 16): If any information we hold about you is inaccurate or incomplete, you can ask us to correct it. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Rectification Request" and describe what needs to be corrected.
•        Right to Erasure / Right to be Forgotten (Art. 17): You can request that we delete your personal data where it is no longer necessary for the purpose it was collected, where you have withdrawn consent, or where we have processed it unlawfully. Note: We may retain some data where required by law. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Erasure Request".
•        Right to Restriction of Processing (Art. 18): You can ask us to pause processing of your data. For example, while you contest its accuracy or while your objection is being considered. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Restriction Request" and explain the circumstances.
•        Right to Data Portability (Art. 20): Where processing is based on consent or contract, you can request that we provide your data in a structured, commonly used, machine-readable format (e.g., JSON or CSV) so you can transfer it to another service. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Portability Request".
•        Right to Object (Art. 21): You have the right to object at any time to processing based on our legitimate interests (Section 3.1), including profiling, and to processing for direct marketing purposes. We must stop unless we have compelling, legitimate grounds. How to exercise: Email feedback@appspacesolutions.in with the subject "GDPR Objection".
•        Right to Withdraw Consent (Art. 7(3)): Where processing is based on consent, you may withdraw it at any time without penalty. Withdrawal does not affect the lawfulness of processing before the withdrawal. How to exercise: See Section 3.2 for feature-specific opt-out steps, or email feedback@appspacesolutions.in with the subject "Withdraw Consent".
•        Rights Related to Automated Decision-Making (Art. 22): We do not use solely automated decision-making (including profiling) that produces legal or similarly significant effects on you. If this changes, we will update this policy and seek your consent where required.

How to submit a request: Email feedback@appspacesolutions.in. Since we do not maintain user accounts, we will verify your identity using available identifiers such as device information or purchase records. We respond within 30 days (extendable to 3 months for complex requests, with prior notice).
Right to complain: If you are unsatisfied with our response, you have the right to lodge a complaint with your local supervisory authority. For example, the ICO (UK), CNIL (France), BfDI (Germany), or your national DPA. A full list is available at: https://edpb.europa.eu/about-edpb/about-edpb/members_en
8.2 Rights Under CCPA/CPRA (California Residents)
If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA), grants you the following rights:
•        Right to Know: You may request that we disclose: the categories and specific pieces of personal information collected about you; the categories of sources; the business or commercial purpose for collecting it; and the categories of third parties with whom we share it. How to exercise: Email feedback@appspacesolutions.in with the subject "CCPA — Right to Know" or use the in-app Privacy Settings menu.
•        Right to Delete: You may request that we delete personal information we have collected about you, subject to certain legal exceptions (e.g., completing transactions, security obligations). How to exercise: Email feedback@appspacesolutions.in with the subject "CCPA — Delete My Data".
•        Right to Correct: You may request correction of inaccurate personal information we maintain about you. How to exercise: Email feedback@appspacesolutions.in with the subject "CCPA — Correct My Data".
•        Right to Opt-Out of Sale or Sharing: We do not sell your personal information, nor do we share it for cross-context behavioral advertising. Should this change, we will update this policy and provide a clear "Do Not Sell or Share My Personal Information" link in the App settings and on our website.
•        Right to Limit Use of Sensitive Personal Information: You may direct us to limit our use of sensitive personal information to what is strictly necessary to provide the App's services. How to exercise: Email feedback@appspacesolutions.in with the subject "CCPA — Limit Sensitive Data Use".
•        Right to Non-Discrimination: We will not deny you services, charge you different prices, or provide a different level of service because you exercised any of these rights.

How to submit a CCPA request: Email feedback@appspacesolutions.in. Requests will be verified using available identifiers. We respond within 45 days (extendable by a further 45 days with notice). You may designate an authorized agent by providing written authorization.
Categories of personal information collected in the last 12 months: Identifiers (device IDs, IP address), commercial information (purchase records), internet/network activity (usage logs), and inferences for analytics. We do not collect sensitive personal information as defined by CPRA beyond what is needed for App functionality.
8.3 Rights Under Other Applicable Laws
We respect privacy rights globally. Below are your rights and how to exercise them under each additional framework:
Canada — PIPEDA & Quebec Law 25
Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and Quebec's Law 25 (Act respecting the protection of personal information in the private sector), you have the right to:
•        Access the personal information we hold about you and receive an account of how it has been used and disclosed.
•        Challenge the accuracy and completeness of your information and have it amended.
•        Withdraw consent to the collection, use, or disclosure of your information (subject to legal and contractual restrictions), with reasonable notice.
•        File a complaint with the Office of the Privacy Commissioner of Canada at https://priv.gc.ca , or the Commission d'accès à l'information du Québec (CAI) for Quebec residents.
How to exercise: Email feedback@appspacesolutions.in with the subject "Canada Privacy Request". We respond within 30 days.
Brazil — LGPD
Under Brazil's Lei Geral de Proteção de Dados Pessoais (LGPD), you have the following rights under Article 18:
•        Confirmation of whether we process your data; access to your data.
•        Correction of incomplete, inaccurate, or outdated data.
•        Anonymization, blocking, or deletion of unnecessary or excessive data.
•        Portability of your data to another service provider.
•        Deletion of data processed with your consent.
•        Information about third parties with whom we share your data.
•        Withdrawal of consent at any time, with information about the consequences.
•        Right to petition the Autoridade Nacional de Proteção de Dados (ANPD) at https://www.gov.br/anpd .
How to exercise: Email feedback@appspacesolutions.in with the subject "LGPD Request". We respond within 15 days as required by the ANPD.
India — DPDP Act 2023
Under India's Digital Personal Data Protection Act 2023, you have the right to:
•        Access a summary of personal data being processed and the purposes for processing.
•        Correction and erasure of your personal data.
•        Grievance redressal: raise a complaint with our designated Grievance Officer (see Section 12).
•        Nominate another individual to exercise your rights on your behalf in the event of your death or incapacity.
How to opt out: You may withdraw consent for non-essential data processing at any time via App Settings → Privacy, or by emailing feedback@appspacesolutions.in with the subject "India DPDP — Withdraw Consent". We respond within a reasonable timeframe as prescribed by the Data Protection Board.
Australia — Privacy Act 1988
Under the Australian Privacy Principles (APPs) in the Privacy Act 1988, you have the right to:
•        Access personal information we hold about you (APP 12).
•        Request correction of personal information that is inaccurate, out of date, incomplete, or misleading (APP 13).
•        Make a complaint about a breach of the APPs. We will respond within 30 days and, if unresolved, you may escalate to the Office of the Australian Information Commissioner (OAIC) at https://oaic.gov.au .
How to opt out of direct marketing (if applicable): Email feedback@appspacesolutions.in with the subject "Australia — Opt Out of Marketing".
How to exercise other rights: Email feedback@appspacesolutions.in with the subject "Australia Privacy Request".
UAE — Federal Decree-Law No. 45 of 2021
Under the UAE's Personal Data Protection Law (PDPL), you have the right to:
•        Be informed about the processing of your personal data.
•        Access your personal data and obtain a copy.
•        Request correction of inaccurate data.
•        Request destruction (deletion) of data no longer needed for its original purpose.
•        Object to processing that causes you damage.
•        Withdraw consent for processing based on consent.
How to exercise: Email feedback@appspacesolutions.in with the subject "UAE PDPL Request". Complaints may be referred to the UAE Data Office.
9. Children's Privacy
Page Bloom is not directed to, and we do not knowingly collect personal information from, children under the age of 13 (or 16 in the EEA and UK, as applicable). If you are a parent or guardian and believe we have inadvertently collected information from a child, please contact us immediately at feedback@appspacesolutions.in, and we will promptly delete such information.
If you are between 13 and 18 years of age, please review this policy with your parent or guardian before using the App.
10. Data Protection Officer
We have appointed a Data Protection Officer (DPO) to oversee compliance with this Privacy Policy and applicable data protection laws. You may contact our DPO at:
•        Email: feedback@appspacesolutions.in
•     Contact No.: +91 8433400233
11. Changes to This Privacy Policy
We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
•        Posting the updated policy in the App with a new "Last Updated" date.
•        Sending an in-app notification or email to registered users for significant changes.
•        Obtaining fresh consent where required by applicable law.
We encourage you to review this policy periodically. Your continued use of the App after changes are posted constitutes your acceptance of the updated policy, to the extent permitted by applicable law.
12. Contact Us
If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at email: feedback@appspacesolutions.in
For complaints, you have the right to contact your local data protection supervisory authority:
•        EEA Users: Your national Data Protection Authority (DPA).
•        UK Users: The Information Commissioner's Office (ICO) at https://ico.org.uk
•        California Users: California Attorney General's Office.
•        Australian Users: Office of the Australian Information Commissioner (OAIC) at https://oaic.gov.au
•        Brazilian Users: Autoridade Nacional de Proteção de Dados (ANPD).
•        Indian Users: Data Protection Board of India (once constituted under DPDP Act).`;

function isMainHeading(line) {
  return (
    line === "Privacy Policy" ||
    line === "Overview" ||
    line === "Information We Collect" ||
    /^\d+\.\s/.test(line)
  );
}

function isSubHeading(line) {
  return /^\d+\.\d+\s/.test(line);
}

function isNestedSubHeading(line) {
  return /^\d+\.\d+\.\d+\s/.test(line);
}

const headingIds = {
  "1. Information We Collect": "information-we-collect",
  "2. How We Use Your Information": "how-we-use-information",
  "3. Legal Basis for Processing (GDPR)": "legal-basis-for-processing-gdpr",
  "4. Application Permission Requirements": "application-permission-requirements",
  "5. How We Share Your Information": "how-we-share-your-information",
  "6. Data Retention": "data-retention",
  "7. Data Security": "data-security",
  "8. Your Privacy Rights": "your-privacy-rights",
  "9. Children's Privacy": "childrens-privacy",
  "10. Data Protection Officer": "data-privacy-officer",
  "11. Changes to This Privacy Policy": "changes-to-this-privacy-policy",
  "12. Contact Us": "contact-us",
};

const headingLabels = {
  "Information We Collect": "1. Information We Collect",
};

const headingFourLines = new Set([
  "Facebook Analytics (by Facebook Inc.)",
  "Firebase Analytics",
  "Tenjin.io (by Tenjin, Inc.)",
  "Adjust (by Adjust GmbH.)",
  "AppsFlyer (by AppsFlyer)",
  "IronSource (by IronSource Mobile Ltd.)",
  "MoPub (Parent company- AppLovin)",
  "Facebook Audience Network (by Facebook Inc.)",
  "AdMob (by Google Inc.)",
  "AppLovin (by AppLovin Corporation)",
  "HyprMx (by HyprMX Mobile LLC.)",
  "Pangle (by Bytedance Pte.Ltd.)",
  "Fyber (now Digital Turbine)",
  "Vungle (by Vungle Inc.)",
  "Mintegral (by Mintegral International Ltd.)",
  "Ogury (by Ogury Limited)",
  "Tapjoy (by Tapjoy Inc.)",
  "Chartboost (by Chartboost Inc.)",
  "AdColony",
  "InMobi (by InMobi Pvt. Ltd.)",
  "Start.io (by Start.io Inc.)",
  "Smaato (by Smaato, Inc.)",
  "Verizon (by Verizon Communications Inc.)",
  "Google AdSense & DoubleClick Cookie",
  "Google Ads",
  "Facebook Ads",
  "Facebook -",
  "Instagram -",
  "X (formerly Twitter) -",
  "LinkedIn -",
  "YouTube -",
  "Pinterest -",
  "Discord -",
]);

const overviewLinks = {
  "Information We Collect": "information-we-collect",
  "How We Use Information": "how-we-use-information",
  "Legal Basis for Processing (GDPR)": "legal-basis-for-processing-gdpr",
  "Application Permission Requirements": "application-permission-requirements",
  "How We Share Your Information": "how-we-share-your-information",
  "Data Retention": "data-retention",
  "Data Security": "data-security",
  "Your Privacy Rights": "your-privacy-rights",
  "Children’s Privacy": "childrens-privacy",
  "Data Privacy Officer": "data-privacy-officer",
  "Changes to This Privacy Policy": "changes-to-this-privacy-policy",
  "Contact Us": "contact-us",
};

function renderLinkedText(text) {
  const linkRegex =
    /(https?:\/\/[^\s)]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(linkRegex);

  return parts.map((part, index) => {
    if (part.startsWith("http://") || part.startsWith("https://")) {
      return (
        <a
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
          key={`${part}-${index}`}
        >
          {part}
        </a>
      );
    }

    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(part)) {
      return <EmailLink key={`mailto-${part}-${index}`} email={part} />;
    }

    return part;
  });
}

function renderPolicyLine(line, index, isOverviewItem = false) {
  if (!line.trim()) {
    return <div className="h-4" key={index} />;
  }

  if (isOverviewItem) {
    const id = overviewLinks[line];

    return (
      <li className="text-blue-600 text-base" key={index}>
        {id ? (
          <a href={`#${id}`} className="hover:underline">
            {line}
          </a>
        ) : (
          line
        )}
      </li>
    );
  }

  if (line.startsWith("•") || line.startsWith("◦")) {
    return (
      <p className="ml-6 mt-2" key={index}>
        {renderLinkedText(line)}
      </p>
    );
  }

  if (isMainHeading(line)) {
    const isTitle = line === "Privacy Policy";
    const label = headingLabels[line] || line;
    const id = headingIds[label] || headingIds[line];
    return isTitle ? (
      <h1 className="mt-20 font-bold text-5xl text-gray-800" key={index}>
        {label}
      </h1>
    ) : (
      <h2 id={id} className="mt-10 font-bold text-3xl text-gray-800" key={index}>
        {label}
      </h2>
    );
  }

  if (isSubHeading(line)) {
    return (
      <h3 className="mt-6 font-semibold text-2xl text-gray-800" key={index}>
        {line}
      </h3>
    );
  }

  if (isNestedSubHeading(line)) {
    return (
      <h4 className="mt-6 font-semibold text-xl text-gray-800" key={index}>
        {line}
      </h4>
    );
  }

  if (headingFourLines.has(line)) {
    return (
      <h5 className="mt-5 ml-4 font-semibold text-lg text-gray-800" key={index}>
        {line}
      </h5>
    );
  }

  return (
    <p className="mt-3" key={index}>
      {renderLinkedText(line)}
    </p>
  );
}

function isHeadingFourContent(lines, index) {
  for (let i = index - 1; i >= 0; i -= 1) {
    const previousLine = lines[i];

    if (!previousLine.trim()) {
      continue;
    }

    if (headingFourLines.has(previousLine)) {
      return true;
    }

    if (isMainHeading(previousLine) || isSubHeading(previousLine) || isNestedSubHeading(previousLine)) {
      return false;
    }
  }

  return false;
}

function renderPolicyContent() {
  const lines = policyContent.split("\n");
  const overviewStart = lines.findIndex((line) => line === "Overview");
  const overviewEnd = overviewStart + 13;

  return lines.map((line, index) => {
    if (index === overviewStart) {
      return (
        <div className="mt-10 bg-gray-50 border border-gray-200 rounded-xl p-6" key={index}>
          <h2 className="font-bold text-2xl text-gray-800 mb-4">Overview of Privacy Policy</h2>
          <ol className="list-decimal list-inside space-y-2">
            {lines
              .slice(overviewStart + 1, overviewEnd)
              .map((overviewLine, overviewIndex) =>
                renderPolicyLine(overviewLine, `overview-${overviewIndex}`, true)
              )}
          </ol>
        </div>
      );
    }

    const isOverviewItem =
      overviewStart !== -1 &&
      overviewEnd !== -1 &&
      index > overviewStart &&
      index < overviewEnd;

    if (isOverviewItem) {
      return null;
    }

    const renderedLine = renderPolicyLine(line, index);

    if (
      isHeadingFourContent(lines, index) &&
      renderedLine?.type === "p" &&
      !line.startsWith("•") &&
      !line.startsWith("◦")
    ) {
      return React.cloneElement(renderedLine, {
        className: `${renderedLine.props.className || ""} ml-4`.trim(),
      });
    }

    return renderedLine;
  });
}

export default function PDFReader() {
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Page Bloom | Privacy Policy</title>
        <meta
          name="description"
          content="Privacy Policy for Page Bloom PDF Reader by AppSpace Solutions."
        />
      </Helmet>
      <AppHeader name="privacy" indexSlide={0} />
      <nav className="flex flex-col items-center md:flex-row md:justify-center gap-4 pt-20">
        <img
          src={pdfReader}
          alt="Page Bloom PDF Reader"
          className="w-20 h-20 md:w-30 md:h-30 rounded-lg"
        />
        <h1 className="mt-2 md:mt-5 font-bold text-3xl md:text-4xl text-gray-800 text-center">
          Page Bloom
        </h1>
      </nav>

      <main>
        <div className="w-5/6 md:w-3/4 px-1 break-words md:px-0 m-auto text-gray-700 text-xl">
          {renderPolicyContent()}
        </div>
      </main>
    </>
  );
}
